import { useScrollAnimation } from "../../../hooks/useScrollAnimation";
import { Link } from "react-router-dom";

const benefits = [
  {
    icon: "ri-palette-line",
    title: "Modern, professional design",
    desc: "Clean, conversion-focused layouts that reflect your brand and make an instant great impression on every visitor.",
  },
  {
    icon: "ri-search-2-line",
    title: "SEO-ready from day one",
    desc: "Every site we build follows Google best practices so you start ranking for the keywords your customers are searching for.",
  },
  {
    icon: "ri-smartphone-line",
    title: "Mobile-friendly websites",
    desc: "Over 60% of web traffic is mobile. Your site looks and works perfectly on phones, tablets, and desktops.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Fast loading websites",
    desc: "Slow sites kill conversions. We optimise every page for speed so visitors stay engaged and Google rewards you.",
  },
];

export default function SolutionSection() {
  const { ref, visible } = useScrollAnimation();

  return (
    <section className="bg-stone-50 section-pad" ref={ref}>
      <div className="container-md">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <div
            className={`transition-all duration-700 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <span className="text-accent text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
              The Solution
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-zinc-900 leading-tight mb-6">
              Websites designed to
              <br />
              <span className="text-accent">grow your business</span>
            </h2>
            <p className="text-zinc-600 text-base md:text-lg leading-relaxed mb-8">
              We specialise in <strong>website design for small businesses</strong> — crafting lead generation websites that don&apos;t just look great, but actually work hard for your business 24/7.
            </p>

            <div className="space-y-5">
              {benefits.map((b, i) => (
                <div
                  key={b.title}
                  className={`flex gap-4 items-start transition-all duration-500 ${
                    visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  }`}
                  style={{ transitionDelay: `${150 + i * 100}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-lg shrink-0 mt-0.5">
                    <i className={`${b.icon} text-xl text-accent`} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-zinc-900 mb-1">{b.title}</h3>
                    <p className="text-zinc-500 text-sm leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <Link
                to="/services"
                className="whitespace-nowrap inline-flex items-center gap-2 text-accent font-semibold hover:gap-3 transition-all duration-200 cursor-pointer group"
              >
                See all our services
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </div>

          {/* Right: Image */}
          <div
            className={`relative transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden w-full h-[480px] md:h-[560px]">
              <img
                src="https://public.readdy.ai/ai/img_res/dcf753d23bf2249e7ff28d9b8010c0c2.jpg"
                alt="Professional business website design on multiple devices"
                className="w-full h-full object-cover object-top"
              />
              {/* Floating stats card */}
              <div className="absolute bottom-6 left-6 bg-white rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg">
                    <i className="ri-arrow-up-line text-green-600 text-lg" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-zinc-900 text-lg leading-none">+247%</p>
                    <p className="text-zinc-500 text-xs mt-0.5">Avg. increase in leads</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Decorative element */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent/10 rounded-full -z-10" />
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-stone-200 rounded-full -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
}
